/**
 * sl-m82f.4.1: Thin REST client for the workspace analytics endpoints.
 *
 * Exists so every hook/tab goes through the same fetch wrapper (credentials,
 * error handling). Keeps the components free of fetch boilerplate.
 */
import { defaultWorkspaceApiClient } from '@affine/core/modules/cloud';

import type { AnalyticsPeriodRange, FeatureTotalsResponse } from './types';

// ─── Generic helpers ────────────────────────────────────────────────────────

async function getJson<T>(workspaceId: string, url: string): Promise<T> {
  return defaultWorkspaceApiClient.requestJson<T>({
    workspaceId,
    path: url,
    category: 'settings',
    key: `GET:${url}`,
    mode: 'background',
  });
}

function usageBase(workspaceId: string): string {
  return `/api/workspace/${workspaceId}/ai/usage`;
}

function analyticsBase(workspaceId: string): string {
  return `/api/workspace/${workspaceId}/ai/analytics`;
}

function workflowBase(workspaceId: string): string {
  return `/api/workspace/${workspaceId}/workflow/usage`;
}

function periodKey(period: string | AnalyticsPeriodRange): string {
  return typeof period === 'string' ? period : period.key;
}

function appendPeriodParams(
  url: string,
  period: string | AnalyticsPeriodRange
): string {
  const params = new URLSearchParams({ month: periodKey(period) });
  if (typeof period !== 'string' && period.since && period.until) {
    params.set('since', period.since);
    params.set('until', period.until);
  }
  return `${url}?${params.toString()}`;
}

function analyticsRowsUrl(
  workspaceId: string,
  resource: 'users' | 'workflows',
  month: string | AnalyticsPeriodRange,
  capability?: string
): string {
  let url = appendPeriodParams(
    `${analyticsBase(workspaceId)}/${resource}`,
    month
  );
  if (capability) {
    url += `&capability=${encodeURIComponent(capability)}`;
  }
  return url;
}

function fetchAnalyticsRows<T>(
  workspaceId: string,
  resource: 'users' | 'workflows',
  month: string | AnalyticsPeriodRange,
  capability?: string
): Promise<T[]> {
  return getJson<T[]>(
    workspaceId,
    analyticsRowsUrl(workspaceId, resource, month, capability)
  );
}

// ─── Feature totals (sl-m82f.2.3) ───────────────────────────────────────────

export async function fetchFeatureTotals(
  workspaceId: string,
  period: string | AnalyticsPeriodRange
): Promise<FeatureTotalsResponse> {
  const params = new URLSearchParams({ period: periodKey(period) });
  if (typeof period !== 'string' && period.since && period.until) {
    params.set('since', period.since);
    params.set('until', period.until);
  }
  const url = `${usageBase(workspaceId)}/feature-totals?${params.toString()}`;
  return getJson<FeatureTotalsResponse>(workspaceId, url);
}

// ─── AI Chat tab (capability ∈ chat/image/audio/tool) ───────────────────────

/**
 * sl-m82f.4.2: Row shape returned by the `/ai/analytics/users` endpoint.
 * The endpoint yields one row per (user, model) pair — so a user with
 * two models appears twice.
 */
export interface AiUserUsageRow {
  userId: string;
  userName: string;
  model: string;
  totalCredits: number;
  totalCostMicro: number;
  callCount: number;
}

/**
 * Canonical analytics filter for the `ai_chat` feature bucket. The backend
 * maps both `chat` and tool-loop `tool` calls to the same `ai_chat`
 * feature_bucket, so one bucket request is the complete AI Chat user view.
 */
export const AI_CHAT_BUCKET_CAPABILITY = 'chat' as const;

/**
 * Fetch per-user × per-model rows for the AI Chat feature bucket.
 * `capability=chat` is intentionally the only request: backend aggregation
 * filters by feature_bucket, and `chat` resolves to the full `ai_chat` bucket
 * including tool-loop rows. Calling `tool` as well would count the same
 * feature_bucket twice.
 */
export async function fetchAiChatUsers(
  workspaceId: string,
  month: string | AnalyticsPeriodRange
): Promise<AiUserUsageRow[]> {
  const rows = await fetchAnalyticsRows<AiUserUsageRow>(
    workspaceId,
    'users',
    month,
    AI_CHAT_BUCKET_CAPABILITY
  );
  return [...rows].sort((a, b) => b.totalCredits - a.totalCredits);
}

// ─── Generic "items" response for embedding/memory/kg ───────────────────────

interface LabeledItem {
  sourceId: string | null;
  label: string;
  credits: number;
  callCount: number;
}

export interface SourceEntityUsageRow {
  sourceEntityType: 'marketplace_template' | 'agent';
  sourceId: string;
  label: string;
  model: string | null;
  credits: number;
  totalCostMicro: number;
  callCount: number;
}

export async function fetchEmbeddingByDoc(
  workspaceId: string,
  month: string | AnalyticsPeriodRange
): Promise<LabeledItem[]> {
  const data = await getJson<{ docs?: LabeledItem[] }>(
    workspaceId,
    appendPeriodParams(`${usageBase(workspaceId)}/embedding-by-doc`, month)
  );
  return data.docs ?? [];
}

export async function fetchMemoryByBot(
  workspaceId: string,
  month: string | AnalyticsPeriodRange
): Promise<LabeledItem[]> {
  const data = await getJson<{ bots?: LabeledItem[] }>(
    workspaceId,
    appendPeriodParams(`${usageBase(workspaceId)}/memory-by-bot`, month)
  );
  return data.bots ?? [];
}

export async function fetchKgByDoc(
  workspaceId: string,
  month: string | AnalyticsPeriodRange
): Promise<LabeledItem[]> {
  const data = await getJson<{ docs?: LabeledItem[] }>(
    workspaceId,
    appendPeriodParams(`${usageBase(workspaceId)}/kg-by-doc`, month)
  );
  return data.docs ?? [];
}

export async function fetchSourceEntityUsage(
  workspaceId: string,
  month: string | AnalyticsPeriodRange,
  entityType: 'marketplace_template' | 'agent',
  featureBucket?: string
): Promise<SourceEntityUsageRow[]> {
  let url = appendPeriodParams(
    `${usageBase(workspaceId)}/source-entities`,
    month
  );
  url += `&entityType=${entityType}`;
  if (featureBucket) {
    url += `&featureBucket=${encodeURIComponent(featureBucket)}`;
  }
  const data = await getJson<{ sources?: SourceEntityUsageRow[] }>(
    workspaceId,
    url
  );
  return data.sources ?? [];
}

// ─── Capability-specific user fetchers (sl-4wjh.2) ─────────────────────────

/**
 * Fetch per-user × per-model rows filtered to a single capability.
 * Used by ImageGen, Transcription, and Websearch tabs.
 */
export async function fetchCapabilityUsers(
  workspaceId: string,
  month: string | AnalyticsPeriodRange,
  capability: string
): Promise<AiUserUsageRow[]> {
  return fetchAnalyticsRows<AiUserUsageRow>(
    workspaceId,
    'users',
    month,
    capability
  );
}

/**
 * sl-18pc.5: Row shape returned by the `/ai/analytics/workflows` endpoint.
 */
export interface WorkflowUsageRow {
  sourceId: string;
  workflowName: string;
  model: string;
  totalCredits: number;
  totalCostMicro: number;
  callCount: number;
}

/**
 * sl-18pc.5: Fetch per-workflow × per-model rows, optionally filtered by
 * capability. Used by the Workflow context tab.
 */
export async function fetchCapabilityWorkflows(
  workspaceId: string,
  month: string | AnalyticsPeriodRange,
  capability?: string
): Promise<WorkflowUsageRow[]> {
  return fetchAnalyticsRows<WorkflowUsageRow>(
    workspaceId,
    'workflows',
    month,
    capability
  );
}

/**
 * sl-18pc.5: Fetch workflow-context AI usage. Workflow context is bucketized
 * as `workflows` before capability routing, so applying `chat` or `tool` would
 * filter out the workflow bucket instead of narrowing it.
 */
export async function fetchAiChatWorkflows(
  workspaceId: string,
  month: string | AnalyticsPeriodRange
): Promise<WorkflowUsageRow[]> {
  const rows = await fetchAnalyticsRows<WorkflowUsageRow>(
    workspaceId,
    'workflows',
    month
  );
  return [...rows].sort((a, b) => b.totalCredits - a.totalCredits);
}

// ─── Workflows (sl-7q0s) ────────────────────────────────────────────────────

export interface WorkflowUsageItem {
  workflowId: string;
  workflowName: string;
  credits: number;
  executions: number;
}

export interface WorkflowUserUsageItem {
  /** Null only when both the executor AND the automation owner are unknown. */
  userId: string | null;
  credits: number;
  executions: number;
  /**
   * True when every execution rolled into this row was triggered by
   * webhook/cron (no interactive executor) and has been attributed to
   * the automation's owner.
   */
  automated: boolean;
}

export async function fetchWorkflowsByWorkflow(
  workspaceId: string,
  month: string | AnalyticsPeriodRange
): Promise<WorkflowUsageItem[]> {
  const data = await getJson<{ workflows?: WorkflowUsageItem[] }>(
    workspaceId,
    appendPeriodParams(`${workflowBase(workspaceId)}/by-workflow`, month)
  );
  return data.workflows ?? [];
}

export async function fetchWorkflowsByUser(
  workspaceId: string,
  month: string | AnalyticsPeriodRange
): Promise<WorkflowUserUsageItem[]> {
  const data = await getJson<{ users?: WorkflowUserUsageItem[] }>(
    workspaceId,
    appendPeriodParams(`${workflowBase(workspaceId)}/by-user`, month)
  );
  return data.users ?? [];
}

export type WorkflowTriggerSource =
  | 'manual'
  | 'webhook'
  | 'cron'
  | 'test'
  | 'trigger'
  | 'unknown';

export interface WorkflowExecutionUser {
  id: string;
  name: string | null;
  email: string | null;
  avatarUrl: string | null;
}

export interface WorkflowExecutionDetail {
  id: string;
  executionId: string | null;
  workflowId: string | null;
  workflowName: string | null;
  docId: string | null;
  docName: string | null;
  automationName: string | null;
  triggerSource: WorkflowTriggerSource;
  executor: WorkflowExecutionUser | null;
  owner: WorkflowExecutionUser | null;
  /**
   * Credits for this execution = ONLY workflow-minute billing
   * (source_entity_type='workflow'). AI-step costs attributed to the same
   * execution belong in the AI analytics tab, not this table.
   */
  credits: number;
  durationMs: number | null;
  status: string | null;
  createdAt: string;
}

export interface WorkflowExecutionsPage {
  executions: WorkflowExecutionDetail[];
  totalCount: number;
}

export async function fetchWorkflowExecutionsDetail(
  workspaceId: string,
  month: string | AnalyticsPeriodRange,
  limit = 20,
  offset = 0
): Promise<WorkflowExecutionsPage> {
  const data = await getJson<{
    executions?: WorkflowExecutionDetail[];
    totalCount?: number;
  }>(
    workspaceId,
    `${appendPeriodParams(`${workflowBase(workspaceId)}/executions-detail`, month)}&limit=${limit}&offset=${offset}`
  );
  return {
    executions: data.executions ?? [],
    totalCount: data.totalCount ?? 0,
  };
}
